import { CompanySize, VerifierType } from 'services/EpdClient';
import { AdminCompanySearchRequest } from 'types/admin-search-companies/AdminCompanySearchRequest';

enum QueryKey {
  NAME = 'q',
  COUNTRY_IDS = 'country',
  SIZE = 'size',
  ROLE_ID = 'role',
  VERIFIER_TYPE = 'verifier',
  INCLUDE_ARCHIVED = 'archived',
  ONLY_HOLDING = 'holding',
  ONLY_PROCESS_CERTIFICATION = 'procCert',
  PAGE_NUMBER = 'page',
  PAGE_SIZE = 'pageSize',
}

export const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

const AdminSearchCompaniesQueryHelper = {
  parse(query: string): AdminCompanySearchRequest {
    const searchParams = new URLSearchParams(query);

    return {
      name: searchParams.get(QueryKey.NAME) || '',
      countryIds: searchParams.get(QueryKey.COUNTRY_IDS)?.split('_') || [],
      size: getEnumOrNull(searchParams, QueryKey.SIZE, CompanySize),
      roleId: searchParams.get(QueryKey.ROLE_ID),
      verifierType: getEnumOrNull(searchParams, QueryKey.VERIFIER_TYPE, VerifierType),
      includeArchived: searchParams.get(QueryKey.INCLUDE_ARCHIVED) === 'true',
      onlyHolding: searchParams.get(QueryKey.ONLY_HOLDING) === 'true',
      onlyProcessCertification: searchParams.get(QueryKey.ONLY_PROCESS_CERTIFICATION) === 'true',
      pageNumber: getIntOrDefault(searchParams, QueryKey.PAGE_NUMBER, 1),
      pageSize: getIntOrDefault(searchParams, QueryKey.PAGE_SIZE, DEFAULT_PAGE_SIZE),
    };
  },

  stringify(param: AdminCompanySearchRequest): string {
    const searchParams = new URLSearchParams();

    if (param.name) {
      searchParams.append(QueryKey.NAME, param.name);
    }
    if (param.countryIds.length > 0) {
      searchParams.append(QueryKey.COUNTRY_IDS, param.countryIds.join('_'));
    }
    if (param.size) {
      searchParams.append(QueryKey.SIZE, param.size);
    }
    if (param.roleId) {
      searchParams.append(QueryKey.ROLE_ID, param.roleId);
    }
    if (param.verifierType) {
      searchParams.append(QueryKey.VERIFIER_TYPE, param.verifierType);
    }
    if (param.includeArchived) {
      searchParams.append(QueryKey.INCLUDE_ARCHIVED, 'true');
    }
    if (param.onlyHolding) {
      searchParams.append(QueryKey.ONLY_HOLDING, 'true');
    }
    if (param.onlyProcessCertification) {
      searchParams.append(QueryKey.ONLY_PROCESS_CERTIFICATION, 'true');
    }
    if (param.pageNumber > 1) {
      searchParams.append(QueryKey.PAGE_NUMBER, String(param.pageNumber));
    }
    if (param.pageSize !== DEFAULT_PAGE_SIZE) {
      searchParams.append(QueryKey.PAGE_SIZE, String(param.pageSize));
    }

    return searchParams.size > 0 ? `?${searchParams.toString()}` : '';
  },

  isFilterActive(param: AdminCompanySearchRequest): boolean {
    return AdminSearchCompaniesQueryHelper.stringify({ ...param, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }) !== '';
  },
};
export default AdminSearchCompaniesQueryHelper;

function getEnumOrNull<T extends Record<string, string>>(
  query: URLSearchParams,
  key: string,
  enumObj: T
): T[keyof T] | null {
  const value = query.get(key);
  if (!value) {
    return null;
  }

  const possibleValues = Object.values(enumObj);
  if (!possibleValues.includes(value)) {
    return null;
  }

  return value as T[keyof T];
}

function getIntOrDefault(query: URLSearchParams, key: string, defaultValue: number): number {
  const value = query.get(key);
  if (!value) {
    return defaultValue;
  }
  const asInt = Number(value);
  if (!Number.isSafeInteger(asInt)) {
    return defaultValue;
  }
  return asInt;
}
